import React from "react";
import PropTypes from "prop-types";
import { humanEUR } from "../blocks/format";
import { ADVERSE_COLOR, FAVOURABLE_COLOR, NEUTRAL_COLOR, START_COLOR, END_COLOR } from "./VarianceWaterfallChart";

// Re-export VarianceWaterfallChart's sign-colour constants so this chart's own test (and any
// future consumer) can assert against the SAME finance-grade palette without importing two
// files - see that module for the full colour rationale (adverse red / favourable green /
// neutral grey / start-grey / end-navy).
export { ADVERSE_COLOR, FAVOURABLE_COLOR, NEUTRAL_COLOR, START_COLOR, END_COLOR };

// RiskBridgeChart - Base forecast -> risk legs -> Risk-adjusted upper bridge for the board
// report (SP3 Component C, GET /figures/risk-bridge -> envelope "risk_bridge"). A SIMPLER
// waterfall than VarianceWaterfallChart's PVM bridge: no Volume/Rate sub-segmentation, each
// leg is a single floating bar. Reuses the same finance-grade colour constants (see that file
// for the full rationale) and the same running-cumulative "floatingSpan" positioning idea, but
// re-implements it locally rather than importing buildWaterfallBars because this shape has no
// `steps`/`residual`/driver concept - it's {base_total, legs:[{label,kind,delta}], upper_total,
// band_upper}.
const AXIS_COLOR = "#6b6b6b";
const CONNECTOR_COLOR = "#b7bac0";
const BAND_COLOR = "#8a8f98";

const VIEW_WIDTH = 900;
const VIEW_HEIGHT = 240;
const PLOT_TOP = 28; // room for the value label above the tallest bar
const PLOT_BOTTOM = VIEW_HEIGHT - 44; // room for the category-label row + connector baseline
const PLOT_HEIGHT = PLOT_BOTTOM - PLOT_TOP;
const BAR_GAP_FRACTION = 0.32; // fraction of each slot's width left as gap between bars

// signedCompactEUR - "+€20k" / "-€35k": humanEUR already handles the minus sign for negative
// input, so this passes the absolute value through and prepends the sign itself to avoid a
// double negative, and to force an explicit "+" on non-negative deltas (a bare "€20k" reads as
// a total, not a change). Mirrors VarianceWaterfallChart's own helper of the same name.
function signedCompactEUR(v) {
  const n = Number(v) || 0;
  return `${n >= 0 ? "+" : "-"}${humanEUR(Math.abs(n))}`;
}

// legColor - legs are coloured by delta SIGN, not by `kind`: a positive delta (overage,
// renewal exposure) makes the risk-adjusted total worse, so it's adverse red; a negative delta
// (committed optimization) makes it better, so it's favourable green. Exactly-zero is neutral.
function legColor(delta) {
  return delta > 0 ? ADVERSE_COLOR : delta < 0 ? FAVOURABLE_COLOR : NEUTRAL_COLOR;
}

/**
 * buildRiskBridgeBars - pure helper: turns the risk-bridge envelope into a flat, ordered list
 * of floating bars, each an absolute (bottom, top) pair in value-space.
 *
 * { base_label, base_total, legs: [{label, kind, delta}], upper_label, upper_total, band_upper }
 * (T9 internal/boardfigures/riskbridge.go).
 *
 * - Base bar: from 0 to base_total (it IS the total, not a direction).
 * - One bar per leg, in order, floating on the running cumulative starting at base_total (same
 *   floatingSpan rule as VarianceWaterfallChart: positive delta floats UP, negative floats
 *   DOWN, never swaps branches and renders from 0).
 * - Upper bar: from 0 to upper_total (it IS the total too).
 *
 * Reconciliation (upper_total === base_total + Σlegs.delta) is guaranteed upstream by the
 * backend, not re-derived here - by construction the last leg's running cumulative equals
 * upper_total, so the upper bar's own top and the running total agree whenever the input is
 * internally consistent.
 */
export function buildRiskBridgeBars(data) {
  if (!data || data.base_total == null || !Array.isArray(data.legs)) return [];

  const baseTotal = Number(data.base_total) || 0;
  const upperTotal = Number(data.upper_total) || 0;
  const legs = data.legs;

  const bars = [
    {
      key: "base",
      role: "base",
      label: data.base_label || "Base forecast",
      valueLabel: humanEUR(baseTotal),
      bottom: 0,
      top: baseTotal,
      after: baseTotal,
      color: START_COLOR,
    },
  ];

  let running = baseTotal;
  legs.forEach((leg, i) => {
    const delta = Number(leg?.delta) || 0;
    const bottom = delta >= 0 ? running : running + delta;
    const top = delta >= 0 ? running + delta : running;
    running += delta;
    bars.push({
      key: `leg-${i}`,
      role: "leg",
      kind: leg?.kind,
      label: leg?.label || `Leg ${i + 1}`,
      valueLabel: signedCompactEUR(delta),
      bottom,
      top,
      after: running,
      color: legColor(delta),
    });
  });

  bars.push({
    key: "upper",
    role: "upper",
    label: data.upper_label || "Risk-adjusted upper",
    valueLabel: humanEUR(upperTotal),
    bottom: 0,
    top: upperTotal,
    after: upperTotal,
    color: END_COLOR,
  });

  return bars;
}

const LEGEND_ITEMS = [
  { key: "adverse", label: "Adverse", color: ADVERSE_COLOR },
  { key: "favourable", label: "Favourable", color: FAVOURABLE_COLOR },
  { key: "base", label: "Base", color: START_COLOR },
  { key: "upper", label: "Upper", color: END_COLOR },
];

// StaticLegend - plain HTML swatches, not an interactive chart-library legend (mirrors
// VarianceWaterfallChart's own StaticLegend - this chart only ever renders once into a
// headless-Chrome PDF, so a click-to-toggle control would be dead chrome). The confidence-band
// reference line gets its own dashed swatch since it isn't a bar colour.
function StaticLegend({ showBand }) {
  return (
    <div
      className="risk-bridge-chart-legend"
      role="list"
      aria-label="Chart legend"
      style={{
        display: "flex", gap: "16px", justifyContent: "center", marginTop: "6px",
        fontSize: "9pt", color: AXIS_COLOR, fontFamily: "Georgia, 'Times New Roman', serif",
      }}
    >
      {LEGEND_ITEMS.map((item) => (
        <span key={item.key} role="listitem" style={{ display: "inline-flex", alignItems: "center", gap: "5px" }}>
          <span
            aria-hidden="true"
            style={{ width: "10px", height: "10px", borderRadius: "2px", background: item.color, display: "inline-block" }}
          />
          {item.label}
        </span>
      ))}
      {showBand && (
        <span role="listitem" style={{ display: "inline-flex", alignItems: "center", gap: "5px" }}>
          <span
            aria-hidden="true"
            style={{
              width: "14px", height: "0px", display: "inline-block",
              borderTop: `2px dashed ${BAND_COLOR}`,
            }}
          />
          Confidence-band upper
        </span>
      )}
    </div>
  );
}

StaticLegend.propTypes = {
  showBand: PropTypes.bool,
};

/**
 * RiskBridgeChart - print-static Base forecast -> risk legs -> Risk-adjusted upper bridge for
 * the board report (SP3 Component C).
 *
 * Consumes { base_label, base_total, legs: [{label, kind, delta}], upper_label, upper_total,
 * band_upper } (see buildRiskBridgeBars for the full contract). Optionally renders a dashed
 * horizontal reference line at `band_upper` (the forecast confidence-band upper bound) when it
 * is a positive number, so a reader can see at a glance whether the risk-adjusted upper still
 * sits inside the calibrated band or has broken through it.
 *
 * Print-static: no Tooltip, no hover/zoom, no click handlers - renders once into a
 * headless-Chrome PDF. Every bar's value and category name is drawn as static SVG <text> so the
 * figure is fully legible without any interaction.
 */
export default function RiskBridgeChart({ data }) {
  const bars = buildRiskBridgeBars(data);

  if (bars.length === 0) {
    return (
      <div
        className="risk-bridge-chart-empty"
        style={{ fontSize: "9pt", color: AXIS_COLOR, fontStyle: "italic", padding: "8px 0" }}
      >
        No risk bridge data available.
      </div>
    );
  }

  const bandUpper = Number(data?.band_upper) || 0;
  const showBand = bandUpper > 0;

  const allValues = bars.flatMap((b) => [b.bottom, b.top]);
  if (showBand) allValues.push(bandUpper);
  const maxVal = Math.max(0, ...allValues);
  const minVal = Math.min(0, ...allValues);
  const range = maxVal - minVal || 1;
  const yFor = (v) => PLOT_TOP + PLOT_HEIGHT * (1 - (v - minVal) / range);

  const slotWidth = VIEW_WIDTH / bars.length;
  const barWidth = slotWidth * (1 - BAR_GAP_FRACTION);
  const baselineY = yFor(0);
  const bandY = showBand ? yFor(bandUpper) : null;

  const positioned = bars.map((bar, i) => {
    const xCenter = slotWidth * i + slotWidth / 2;
    const yTop = yFor(bar.top);
    const yBottom = yFor(bar.bottom);
    return {
      ...bar,
      x: xCenter - barWidth / 2,
      xCenter,
      y: Math.min(yTop, yBottom),
      height: Math.max(1, Math.abs(yBottom - yTop)),
    };
  });

  return (
    <div className="risk-bridge-chart" style={{ width: "100%" }}>
      <div style={{ width: "100%", height: VIEW_HEIGHT }}>
        <svg
          width="100%"
          height="100%"
          viewBox={`0 0 ${VIEW_WIDTH} ${VIEW_HEIGHT}`}
          preserveAspectRatio="none"
          role="img"
          aria-label="Forecast risk bridge"
        >
          <line
            x1={0}
            y1={baselineY}
            x2={VIEW_WIDTH}
            y2={baselineY}
            stroke={AXIS_COLOR}
            strokeWidth={1}
          />
          {showBand && (
            <>
              <line
                className="risk-bridge-band-line"
                data-band-value={bandUpper}
                x1={0}
                y1={bandY}
                x2={VIEW_WIDTH}
                y2={bandY}
                stroke={BAND_COLOR}
                strokeWidth={1.5}
                strokeDasharray="6 4"
              />
              <text
                x={VIEW_WIDTH - 4}
                y={bandY - 5}
                textAnchor="end"
                fontSize="9"
                fontFamily="Georgia, 'Times New Roman', serif"
                fill={BAND_COLOR}
              >
                Confidence-band upper ({humanEUR(bandUpper)})
              </text>
            </>
          )}
          {/* Connector lines between consecutive bars - a horizontal dashed thread at the
              running-cumulative value carried from one bar into the next, so a reader can trace
              the walk from base_total to upper_total without doing the arithmetic themselves. */}
          {positioned.slice(0, -1).map((bar, i) => {
            const next = positioned[i + 1];
            const y = yFor(bar.after);
            return (
              <line
                key={`connector-${bar.key}`}
                x1={bar.xCenter + barWidth / 2}
                y1={y}
                x2={next.xCenter - barWidth / 2}
                y2={y}
                stroke={CONNECTOR_COLOR}
                strokeWidth={1}
                strokeDasharray="3 3"
              />
            );
          })}
          {positioned.map((bar) => (
            <g key={bar.key}>
              <rect
                className="risk-bridge-bar"
                data-role={bar.role}
                data-kind={bar.kind}
                data-leg-label={bar.role === "leg" ? bar.label : undefined}
                x={bar.x}
                y={bar.y}
                width={barWidth}
                height={bar.height}
                fill={bar.color}
              />
              <text
                x={bar.xCenter}
                y={bar.y - 8}
                textAnchor="middle"
                fontSize="11"
                fontFamily="Georgia, 'Times New Roman', serif"
                fill={AXIS_COLOR}
              >
                {bar.valueLabel}
              </text>
              <text
                x={bar.xCenter}
                y={PLOT_BOTTOM + 20}
                textAnchor="middle"
                fontSize="10"
                fontFamily="Georgia, 'Times New Roman', serif"
                fill={AXIS_COLOR}
              >
                {bar.label}
              </text>
            </g>
          ))}
        </svg>
      </div>
      <StaticLegend showBand={showBand} />
    </div>
  );
}

RiskBridgeChart.propTypes = {
  data: PropTypes.shape({
    base_label: PropTypes.string,
    base_total: PropTypes.number,
    legs: PropTypes.arrayOf(
      PropTypes.shape({
        label: PropTypes.string,
        kind: PropTypes.string,
        delta: PropTypes.number,
      })
    ),
    upper_label: PropTypes.string,
    upper_total: PropTypes.number,
    band_upper: PropTypes.number,
  }),
};
